import React, { useState } from 'react';
import { paletteFor } from '../data/showcaseProjects';

// ──────────────────────────────────────────────
// ProjectPoster
// Renders /posters/{id}.jpg if available, with typography overlay.
// Falls back to SVG-generated poster if image missing or fails to load.
// Variants: 'hero' (large, bold), 'medium' (voting tier), 'compact' (list rows).
// ──────────────────────────────────────────────

export default function ProjectPoster({ project, variant = 'medium' }) {
  const [imgFailed, setImgFailed] = useState(false);
  const palette = paletteFor(project.genre);

  if (!imgFailed) {
    return (
      <PhotoPoster
        project={project}
        palette={palette}
        onFail={() => setImgFailed(true)}
      />
    );
  }
  return <SvgPoster project={project} variant={variant} palette={palette} />;
}

// ──────────────────────────────────────────────
// Photo poster: real image, rendered clean (poster art has its own typography)
// ──────────────────────────────────────────────
function PhotoPoster({ project, palette, onFail }) {
  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      borderRadius: 'inherit',
      overflow: 'hidden',
      background: palette.from, // shows briefly during image load
    }}>
      <img
        src={`/posters/${project.id}.png`}
        alt={`${project.title} poster`}
        onError={onFail}
        loading="lazy"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          display: 'block',
        }}
      />
    </div>
  );
}

// ──────────────────────────────────────────────
// SVG poster fallback (used when image is missing)
// ──────────────────────────────────────────────
function SvgPoster({ project, variant, palette }) {
  const id = project.id;

  const variantConfig = {
    hero: { titleSize: 56, titleY: 460, lineGap: 56, genreSize: 13, directorSize: 11 },
    medium: { titleSize: 44, titleY: 470, lineGap: 44, genreSize: 11, directorSize: 9 },
    compact: { titleSize: 38, titleY: 480, lineGap: 40, genreSize: 10, directorSize: 9 },
  }[variant];

  const lines = project.titleLines || [project.title];
  const totalHeight = (lines.length - 1) * variantConfig.lineGap;
  const startY = variantConfig.titleY - totalHeight;

  return (
    <svg
      viewBox="0 0 400 600"
      preserveAspectRatio="xMidYMid slice"
      style={{ width: '100%', height: '100%', display: 'block', borderRadius: 'inherit' }}
      role="img"
      aria-label={`${project.title} poster`}
    >
      <defs>
        <linearGradient id={`grad-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={palette.from} />
          <stop offset="100%" stopColor={palette.to} />
        </linearGradient>

        <radialGradient id={`spot-${id}`} cx="20%" cy="15%" r="70%">
          <stop offset="0%" stopColor={palette.accent} stopOpacity="0.18" />
          <stop offset="60%" stopColor={palette.accent} stopOpacity="0" />
        </radialGradient>

        <filter id={`grain-${id}`}>
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="3" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
          <feComponentTransfer>
            <feFuncA type="discrete" tableValues="0 0 0 0 0.06 0" />
          </feComponentTransfer>
          <feComposite in2="SourceGraphic" operator="in" />
        </filter>

        <linearGradient id={`fade-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={palette.from} stopOpacity="0" />
          <stop offset="60%" stopColor={palette.from} stopOpacity="0" />
          <stop offset="100%" stopColor={palette.from} stopOpacity="0.85" />
        </linearGradient>
      </defs>

      {/* Base gradient */}
      <rect width="400" height="600" fill={`url(#grad-${id})`} />

      {/* Soft accent spotlight from upper-left */}
      <rect width="400" height="600" fill={`url(#spot-${id})`} />

      {/* Genre-specific motif — subtle large initial */}
      <text
        x="-30"
        y="380"
        fill={palette.accent}
        fontFamily="'Outfit', sans-serif"
        fontWeight="900"
        fontSize="480"
        opacity="0.06"
        letterSpacing="-0.05em"
      >
        {project.title.charAt(0)}
      </text>

      {/* Decorative element per genre */}
      <GenreMotif genre={project.genre} palette={palette} />

      {/* Bottom darken for text legibility */}
      <rect width="400" height="600" fill={`url(#fade-${id})`} />

      {/* Film grain overlay */}
      <rect width="400" height="600" filter={`url(#grain-${id})`} fill={palette.text} />

      {/* Top hairline + genre tag */}
      <line x1="30" y1="48" x2="80" y2="48" stroke={palette.accent} strokeWidth="2" />
      <text
        x="30"
        y="72"
        fill={palette.accent}
        fontFamily="'JetBrains Mono', monospace"
        fontSize={variantConfig.genreSize}
        fontWeight="500"
        letterSpacing="0.18em"
      >
        {project.genre.toUpperCase()}
      </text>

      {/* Title — multi-line */}
      <text
        x="30"
        fill={palette.text}
        fontFamily="'Outfit', sans-serif"
        fontWeight="800"
        fontSize={variantConfig.titleSize}
        letterSpacing="-0.04em"
      >
        {lines.map((line, i) => (
          <tspan key={i} x="30" y={startY + i * variantConfig.lineGap}>
            {line}
          </tspan>
        ))}
      </text>

      {/* Bottom hairline + director credit */}
      <line x1="30" y1="525" x2="60" y2="525" stroke={palette.accent} strokeWidth="1.5" />
      <text
        x="30"
        y="552"
        fill={palette.muted}
        fontFamily="'JetBrains Mono', monospace"
        fontSize={variantConfig.directorSize}
        letterSpacing="0.15em"
      >
        DIRECTED BY {project.director.toUpperCase()}
      </text>
      {project.creditNote && (
        <text
          x="30"
          y="572"
          fill={palette.muted}
          fontFamily="'JetBrains Mono', monospace"
          fontSize={variantConfig.directorSize - 1}
          opacity="0.6"
          letterSpacing="0.12em"
        >
          {project.creditNote.toUpperCase()}
        </text>
      )}
    </svg>
  );
}

function GenreMotif({ genre, palette }) {
  // Subtle decorative element rendered behind the title, varies by genre
  if (genre === 'Documentary' || genre === 'Music Documentary') {
    // Photographic frame motif
    return (
      <g opacity="0.25">
        <rect x="240" y="100" width="130" height="180" fill="none" stroke={palette.accent} strokeWidth="1.5" />
        <rect x="252" y="112" width="106" height="156" fill="none" stroke={palette.accent} strokeWidth="0.5" opacity="0.6" />
      </g>
    );
  }
  if (genre.includes('Sci-Fi')) {
    // Geometric circle + grid
    return (
      <g opacity="0.2">
        <circle cx="305" cy="170" r="70" fill="none" stroke={palette.accent} strokeWidth="1" />
        <circle cx="305" cy="170" r="50" fill="none" stroke={palette.accent} strokeWidth="0.5" />
        <line x1="305" y1="100" x2="305" y2="240" stroke={palette.accent} strokeWidth="0.5" />
        <line x1="235" y1="170" x2="375" y2="170" stroke={palette.accent} strokeWidth="0.5" />
      </g>
    );
  }
  if (genre.includes('Thriller') || genre === 'Horror') {
    // Vertical bars / glitch
    return (
      <g opacity="0.18">
        <rect x="290" y="100" width="2" height="200" fill={palette.accent} />
        <rect x="310" y="120" width="2" height="160" fill={palette.accent} />
        <rect x="330" y="140" width="2" height="180" fill={palette.accent} />
        <rect x="350" y="110" width="2" height="220" fill={palette.accent} />
      </g>
    );
  }
  if (genre === 'Anthology') {
    // Six dots — six directors
    return (
      <g opacity="0.4">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <circle
            key={i}
            cx={260 + (i % 3) * 36}
            cy={140 + Math.floor(i / 3) * 36}
            r="6"
            fill={palette.accent}
            opacity={0.4 + (i * 0.1)}
          />
        ))}
      </g>
    );
  }
  if (genre.includes('Road')) {
    // Horizon line
    return (
      <g opacity="0.3">
        <line x1="0" y1="220" x2="400" y2="220" stroke={palette.accent} strokeWidth="1" />
        <line x1="160" y1="220" x2="200" y2="280" stroke={palette.accent} strokeWidth="1" />
        <line x1="240" y1="220" x2="200" y2="280" stroke={palette.accent} strokeWidth="1" />
      </g>
    );
  }
  if (genre.includes('Drama')) {
    // Horizon / minimalist horizontal lines
    return (
      <g opacity="0.25">
        <line x1="240" y1="180" x2="370" y2="180" stroke={palette.accent} strokeWidth="1" />
        <line x1="250" y1="200" x2="360" y2="200" stroke={palette.accent} strokeWidth="0.5" />
        <line x1="260" y1="220" x2="350" y2="220" stroke={palette.accent} strokeWidth="0.5" opacity="0.6" />
      </g>
    );
  }
  // Comedy or default — bold accent shape
  return (
    <g opacity="0.3">
      <rect x="270" y="120" width="100" height="100" fill={palette.accent} opacity="0.4" />
      <rect x="280" y="140" width="100" height="100" fill="none" stroke={palette.accent} strokeWidth="1.5" />
    </g>
  );
}
